import { MANAGER_ROLES } from "./articleRoles";

const CATEGORY = "Surveys";

export const SURVEY_ARTICLES = [
   {
      id: "create-survey",
      category: CATEGORY,
      title: "Create a survey",
      summary: "Start a survey from scratch, from a template, or from an Excel file.",
      roles: MANAGER_ROLES,
      related: ["build-survey", "import-survey", "survey-templates"],
      body: `
1. Open a project and choose **Surveys** in the sidebar.
2. Click **New survey**. The **How would you like to start?** window offers three ways:
   - **Start from scratch**: an empty survey you build yourself.
   - **Import survey**: upload an Excel (XLSForm) file. See [Import a survey from Excel](#import-survey).
   - **Use a template**: start from a ready-made survey. See [Reuse a survey as a template](#survey-templates).
3. Choose one and click **Continue**.

## Start from scratch

1. Fill in the survey details:
   - **Survey Title** (required) and **Survey Description**.
   - **Default Language** (required): the language you write the questions in.
   - **Survey Manager** (required).
   - **Deadline** (required): after this date the survey stops accepting responses.
   - **Collection Goal** (required): how many responses you aim to collect.
2. Click **Create**. The survey builder opens.

Next, add your questions. See [Build your survey](#build-survey).

> **Tip:** You can change the collection goal later from **Survey Analytics**: open the survey's **Survey Dashboard** and click **Edit**.
`
   },
   {
      id: "build-survey",
      category: CATEGORY,
      title: "Build your survey",
      summary: "Add sections and questions, choose question types and save your work.",
      roles: MANAGER_ROLES,
      related: ["survey-logic", "translate-survey", "publish-and-share"],
      body: `
The survey builder has three tabs: **Form Builder** for the questions, **Logic Builder** for skip logic, and **Translate Questions** for other languages.

> **Important:** The builder doesn't save automatically. Click **Save Progress** before you leave the page or switch tabs. Each tab saves only its own changes.

## Add sections and questions

1. In an empty survey, click **+** to add your first section.
2. Type the section's label.
3. Click **Add new question** inside the section and type the question.
4. Pick the question type from the list at the top-right of the question.
5. Turn on **Required answer** if the question must be answered.
6. Click **Save Progress**.

Use **Add new inner section** to group questions inside a section, and **Add new section** for a new top-level section. Drag a question by its **⋯** handle to move it within its section. Use the copy icon to duplicate a question, and the bin icon to delete it.

## Question types

- **Choices**: **Select one** and **Select multiple**.
- **Text and numbers**: **Text** and **Number**.
- **Dates and times**: **Date**, **Time** and **Date & time**.
- **Contact and location**: **Phone number** and **Geopoint** (GPS location).
- **Files**: **Image upload** and **File upload**.
- **Grids**: **Matrix / Rating** (a choice per row) and **Matrix** (a text box per cell).
- **Other**: **Note** (text with no answer), **Acknowledge** (a checkbox to confirm), and **Calculate** and **Hidden** (values worked out with XLSForm expressions).

## Add answer choices faster

For **Select one** and **Select multiple** questions:

- **Answer genius** adds a ready-made scale, such as Yes - No, Agree - Disagree, days of the week or age ranges.
- **Bulk Actions** lets you paste many choices at once, one per line.
- **Reverse answers order** flips the list.
- On **Select one**, **Add image** puts a picture on a choice (JPG, PNG, WEBP or GIF, up to 2 MB).
- On **Select multiple**, **Maximum number of options that can be chosen** limits how many choices a respondent can pick.

## More options

- **Preview** shows the survey as respondents will see it.
- **Export Survey** downloads it as an Excel file.
- **Track location:** records where responses are collected. This only works on mobile devices.
- The icons in the survey banner open **Design Settings** (logo, header image, background and text colors) and the survey details (title, default language, deadline, manager, description).

> **Tip:** Hints, answer validation and an "Other" choice can't be added in the Form Builder. You can bring them in by importing an XLSForm file. See [Import a survey from Excel](#import-survey).
`
   },
   {
      id: "survey-logic",
      category: CATEGORY,
      title: "Show questions only when they apply",
      summary: "Use the Logic Builder to show a question or section based on earlier answers.",
      roles: MANAGER_ROLES,
      related: ["build-survey"],
      body: `
Skip logic shows a question or a section only when earlier answers match conditions you set. For example, ask "How many children do you have?" only to people who answered "Yes" to "Do you have children?".

> **Tip:** Save your questions in the **Form Builder** before adding logic to them.

## Add a condition

1. Open the **Logic Builder** tab.
2. Click **Show Logic** on the question or section you want to show conditionally.
3. In the condition row (**IF**), pick an earlier question, a comparison and a value.
4. Click **Add Another Condition** for more rows. With two or more rows, choose whether all of them or any of them must match.
5. Click **Save Progress**. You'll see "Skip logics have been saved successfully".

## Good to know

- Conditions can only use questions that come **before** the one you're setting up.
- The comparisons depend on the earlier question's type:
  - **Choice and text questions**: was answered, was not answered, equals, not equals.
  - **Number and date questions**: also greater than, less than, and their "or equal to" versions.
  - **Upload and Acknowledge questions**: was answered or was not answered.
- Unsaved logic changes are lost if you leave the **Logic Builder** tab.
`
   },
   {
      id: "translate-survey",
      category: CATEGORY,
      title: "Translate your survey",
      summary: "Offer your survey in more languages, typed by hand or with KAPinsights AI.",
      roles: MANAGER_ROLES,
      related: ["build-survey", "add-funds"],
      body: `
Respondents can pick the survey's language from the **Survey Language** list on the form. Only **published** translations appear in that list.

## Translate by hand

1. In the survey builder, open the **Translate Questions** tab.
2. Choose a language under **Translate To**.
3. Type the translation of each section, question and answer choice on the right, next to the original.
4. Click **Save translation** to keep your work.
5. When it's complete, click **Publish translation**.

In the language list, a green check marks a published translation and an orange mark a saved one that isn't published yet. To take a translation offline again, click **Archive translation**.

## Translate with KAPinsights AI

The Organization, Senior Managers and Project Managers can translate a survey automatically.

1. Click **Translate with KAPinsights**.
2. Choose the language under **Translate to**. You'll see the estimated cost.
3. Click **Continue**. KAPinsights opens in a new tab, and the cost is charged to your balance.

Translating again into a language that already has a translation overwrites it and is charged again.
`
   },
   {
      id: "import-survey",
      category: CATEGORY,
      title: "Import a survey from Excel",
      summary: "Bring in an existing survey with an XLSForm file.",
      roles: MANAGER_ROLES,
      related: ["create-survey", "build-survey"],
      body: `
If your survey already exists in Excel (XLSForm), for example from KoboToolbox or ODK, you can import it instead of building it again.

1. On the **Surveys** page, click **New survey**, choose **Import survey** and click **Continue**.
2. Click **Download survey format** to get the template, and fill in your survey in the same layout.
3. Drop your file on **Upload module file**. Only .xlsx files, up to 20 MB.
4. Check the **Survey title**, which is filled in from the file name. Pick a **Manager** and a **Deadline**, and optionally add a description.
5. Enter the **Collection goal**.
6. Click **Continue**. You'll see "Survey imported successfully" and the builder opens.

## What an import brings in

Besides groups and questions, an XLSForm can carry things the Form Builder can't create, such as hints, answer constraints with their messages, calculations, default values, choice filters and repeat groups.

> **Tip:** Keep the template's layout exactly as it is, including its first rows and column headings. If the import fails, the message lists what to fix. To start from a working file, use **Export Survey** on an existing survey and edit that.
`
   },
   {
      id: "publish-and-share",
      category: CATEGORY,
      title: "Publish and share your survey",
      summary: "Publish a survey, then share it by link or QR code.",
      roles: MANAGER_ROLES,
      related: ["send-campaign", "assign-data-collection", "survey-analytics"],
      body: `
A new survey is in **Editing** status. Respondents can only submit answers once it's **Published**.

## Publish

In the survey builder, click **Publish Survey**. The survey moves to the **Published** tab on the **Surveys** page.

To make bigger changes, you can switch it back with **Change status to Editing**. Once the survey has responses, assignments or scheduled sends, it can no longer go back to editing. You can still edit a published survey from its menu, but the server won't let you remove answer choices that responses already use.

## Share a link or QR code

1. On the **Surveys** page, click the share icon on a published survey. You can also use **Share survey** in **Survey Analytics**.
2. Choose how people respond:
   - **One Response Per Person**: each copied link works only once. Copy a new link for each respondent.
   - **Multiple responses**: one link that the same person can use again and again.
   - **Kiosk Mode**: for a shared device, such as a tablet at an event. The form resets after each response.
3. Click **Copy link**, or **QR code** to print or download a QR code.

To reach more people, use **Start a campaign** to send the survey by email, SMS or WhatsApp. See [Send a survey by email, SMS or WhatsApp](#send-campaign). Use **Assign to team** to give it to your data collectors. See [Assign a survey to data collectors](#assign-data-collection).

> **Tip:** Test your survey with **Preview** in the builder. A response submitted from a published survey's preview counts as a real response.

## When the survey ends

After the **Deadline**, the survey shows as **Inactive**, and respondents see that it has expired. To set or change the deadline, open the survey details from the icon in the builder's banner.
`
   },
   {
      id: "survey-templates",
      category: CATEGORY,
      title: "Reuse a survey as a template",
      summary: "Save a survey's questions and logic as a template, and start new surveys from it.",
      roles: MANAGER_ROLES,
      related: ["create-survey"],
      body: `
Templates let you reuse a survey's structure, for example for a baseline and an endline. A template copies the questions and logic, not the responses.

## Save a survey as a template

1. On the **Surveys** page, open the **Draft** tab.
2. Open the survey's menu and choose **Turn into template**.
3. Enter a **Template name** (at least 3 characters) and click **Convert**.

## Start a survey from a template

1. Click **New survey**, choose **Use a template**, and click **Continue**.
2. Pick a template and click **Use template**.
3. Check the survey's title, language, manager, deadline and collection goal, then click **Continue**.

> **Tip:** There is no "duplicate survey" button. To copy a survey, turn it into a template and start a new survey from it.

## Manage your templates

Click **Template library** on the **Surveys** page to preview, edit, download or delete your templates. Deleting a template is permanent.
`
   },
   {
      id: "archive-delete-restore",
      category: CATEGORY,
      title: "Archive, delete and restore surveys",
      summary: "Tidy up your survey list and bring back surveys you removed.",
      roles: MANAGER_ROLES,
      related: ["publish-and-share"],
      body: `
## Archive a survey

Archiving moves a survey out of your lists without deleting it.

1. Open the survey's menu and choose **Archive**, or tick several surveys and choose **Archive selected rows**.
2. If the survey has assigned tasks that haven't started, you'll be asked to confirm. Archiving deletes those tasks.

To bring a survey back, open the **⋮** menu at the top of the **Surveys** page, choose **Archived surveys**, and click **Unarchive**.

Archived customer feedback surveys stay on the **Customer feedback** tab. Choose **Unarchive** in their menu; they come back published.

## Delete a survey

Only surveys in **Editing** status (the **Draft** tab) can be deleted. Open the survey's menu and choose **Delete**, or tick several and click **Delete selected rows**.

Customer feedback surveys are always published, and can be deleted from their menu on the **Customer feedback** tab.

## Restore a deleted survey

Deleted surveys are kept for **60 days**. To restore one:

- On the **Draft** tab, open the **⋮** menu and choose **Deleted surveys**, or
- choose **Deleted items** in the project sidebar and open the **Survey** tab.

Click the restore icon on the survey, or **Restore all**. **Delete all** removes them permanently.
`
   }
];
